import { ArrowRightOutlined, CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import AreaTrendChart from '../../../components/Charts/AreaTrendChart';
import RankedBars from '../../../components/Charts/RankedBars';
import StatsCard from '../../../components/Cards/StartsCard';
import DateRangeSelector from '../../../components/DateRangeSelector/DateRangeSelector';
import { Card, CardHead, CardTitle, Segmented } from '../../../components/Layout/PagePieces';
import PageLayout from '../../../components/Layout/PageLayout';
import Page from '../../../components/Page/Page';
import StatusCell from '../../../components/Badge/StatusCell';
import Table from '../../../components/Table/Table';
import CustomerLinkCell from '../../../components/Table/TableAccessories/CustomerLinkCell';
import TableDateTimeFormatter from '../../../components/Table/TableAccessories/TableDateTimeFormatter';
import TableMoneyFormatter from '../../../components/Table/TableAccessories/TableMoneyFormatter';
import useDashboardBreakdown from '../../../hooks/dashboard/overview/useDashboardBreakdown';
import useDashboardOverview from '../../../hooks/dashboard/overview/useDashboardOverview';
import useDashboardTimeseries from '../../../hooks/dashboard/overview/useDashboardTimeseries';
import { useInbox } from '../../../context/InboxContext';
import InfoTip from '../../../components/Help/InfoTip';
import PageGuidePanel, {
  GUIDES_RESET_EVENT,
  hasSeenGuide,
  markGuideSeen,
} from '../../../components/Help/PageGuidePanel';
import { DateRangeOption } from '../../../models/dashboard/overview.model';
import { PAGE_GUIDES } from '../../../models/guides/pageGuides';
import { useAppSelector } from '../../../redux/store';
import { capitalize, formatNaira, formatNumber, getTimeOfDayGreeting, timeAgo } from '../../../utils/utils';
import { DASHBOARD, INBOX, PROFIT, TRANSACTIONS } from '../../../routes/routeConstants';
import { ButtonLink } from '../../../components/Link/Link';
import TransactionDetailSlider from '../Transactions/TransactionDetailSlider';
import FeatureMetrics from './FeatureMetrics';
import ProfitTiles from './ProfitTiles';

type HeroMetric = 'value' | 'revenue' | 'volume';

const RANGE_LABEL: Record<DateRangeOption, string> = {
  today: 'today',
  '7d': 'in the last 7 days',
  '30d': 'in the last 30 days',
  custom: 'in this period',
};

const trendText = (trend?: number) =>
  trend === undefined ? '' : `${trend >= 0 ? '↑' : '↓'} ${Math.abs(trend).toFixed(1)}%`;

const naira = (v?: string | number) => formatNaira(Number(v ?? 0));
const nairaShort = (v: number) =>
  v >= 1_000_000
    ? `₦${Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v)}`
    : formatNaira(v);
const words = (v: string) => capitalize(v.replace(/_/g, ' '));

// The home page, laid out like Stripe's and Mercury's: a greeting, what needs
// attention right now, one headline chart, a few key numbers, then detail.
const Dashboard = () => {
  const [range, setRange] = useState<DateRangeOption>('30d');
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [hero, setHero] = useState<HeroMetric>('value');
  const [showWelcome, setShowWelcome] = useState(() => !hasSeenGuide(DASHBOARD));
  const { authData } = useAppSelector((state) => state.auth);

  const { overview, fetchOverview } = useDashboardOverview();
  const { timeseries: volumeSeries, fetchTimeseries: fetchVolume } = useDashboardTimeseries();
  const { timeseries: valueSeries, fetchTimeseries: fetchValue } = useDashboardTimeseries();
  const { timeseries: revenueSeries, fetchTimeseries: fetchRevenue } = useDashboardTimeseries();
  const { breakdown: byType, fetchBreakdown: fetchByType } = useDashboardBreakdown('by-type');
  const { breakdown: byProvider, fetchBreakdown: fetchByProvider } =
    useDashboardBreakdown('by-provider');
  const inbox = useInbox();

  const handleRangeChange = (newRange: DateRangeOption, newStart?: string, newEnd?: string) => {
    setRange(newRange);
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  useEffect(() => {
    const onReset = () => setShowWelcome(true);
    window.addEventListener(GUIDES_RESET_EVENT, onReset);
    return () => window.removeEventListener(GUIDES_RESET_EVENT, onReset);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (range === 'custom' && (!startDate || !endDate)) {
      return;
    }
    fetchOverview(range, startDate, endDate);
    fetchVolume('volume', range, startDate, endDate);
    fetchValue('value', range, startDate, endDate);
    fetchRevenue('revenue', range, startDate, endDate);
    fetchByType(range, startDate, endDate);
    fetchByProvider(range, startDate, endDate);
    // eslint-disable-next-line
  }, [range, startDate, endDate]);

  // Decimal fields (`value`) come back from the API as strings so precision
  // survives JSON — Recharts can't compute a numeric axis domain from
  // strings, so they must be coerced to numbers before being charted.
  const toPoints = (series: typeof volumeSeries) =>
    (series?.points ?? []).map((point) => ({ date: point.date, value: Number(point.value) }));

  const heroConfig = {
    value: {
      label: 'Transaction value',
      hint: 'The bill amount of every successful purchase. Dash gift payments aren’t counted here; see the Dash gifts tab below.',
      total: naira(overview?.total_transaction_value),
      trend: overview?.trends.total_transaction_value,
      points: toPoints(valueSeries),
      format: nairaShort,
    },
    revenue: {
      label: 'Revenue',
      hint: 'What Hethera kept from successful purchases after the Paystack fee and VTpass cost, including service fees. Claude and WhatsApp costs are in Profit.',
      total: naira(overview?.total_revenue),
      trend: overview?.trends.total_revenue,
      points: toPoints(revenueSeries),
      format: nairaShort,
    },
    volume: {
      label: 'Transactions',
      hint: 'Every bill purchase started in this period, whatever happened to it.',
      total: formatNumber(overview?.total_transactions ?? 0),
      trend: overview?.trends.total_transactions,
      points: toPoints(volumeSeries),
      format: (v: number) => formatNumber(v),
    },
  }[hero];

  const total = overview?.total_transactions ?? 0;
  const outcomes = [
    { key: 'successful', label: 'Successful', value: overview?.successful_transactions ?? 0, color: '#1F7A45' },
    { key: 'pending', label: 'Pending', value: overview?.pending_transactions ?? 0, color: '#D9A21B' },
    { key: 'failed', label: 'Failed', value: overview?.failed_transactions ?? 0, color: '#D92D20' },
  ];
  // Unknown (still being confirmed) and reversed purchases, so the shares add up.
  const other = Math.max(0, total - outcomes.reduce((sum, o) => sum + o.value, 0));
  if (other > 0) {
    outcomes.push({ key: 'other', label: 'Unknown or reversed', value: other, color: '#A9A395' });
  }

  // The top of the Inbox, so the home page shows what needs a person now.
  const attention = inbox.items.slice(0, 3);
  const moreInInbox = inbox.items.length - attention.length;

  const avgPurchase =
    overview && overview.successful_transactions > 0
      ? Number(overview.total_transaction_value) / overview.successful_transactions
      : 0;

  const username = authData?.user?.username;
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Page title='Hethera | Overview'>
      <PageLayout>
        <Header>
          <div>
            <Eyebrow>{today}</Eyebrow>
            <Greeting>
              {getTimeOfDayGreeting()}
              {username ? `, ${username}` : ''}
            </Greeting>
          </div>
          <DateRangeSelector
            value={range}
            onChange={handleRangeChange}
            startDate={startDate}
            endDate={endDate}
          />
        </Header>

        {showWelcome && (
          <PageGuidePanel
            guide={PAGE_GUIDES[DASHBOARD]}
            title='Welcome. Here’s how to read this page'
            onClose={() => {
              markGuideSeen(DASHBOARD);
              setShowWelcome(false);
            }}
          />
        )}

        {inbox.loaded && (
          <Attention aria-label='Needs attention'>
            {attention.length === 0 ? (
              <AllClear>
                <CheckCircleFilled /> All clear. Nothing needs your attention right now.
              </AllClear>
            ) : (
              <>
                {attention.map((item) => (
                  <AttentionItem
                    key={item.id}
                    to={item.action.to}
                    className={item.severity === 'critical' || item.severity === 'high' ? 'red' : 'amber'}
                  >
                    <WarningFilled className='icon' />
                    <span className='text'>
                      <strong>{item.title}</strong>
                      {item.since && <span>{timeAgo(item.since)}</span>}
                    </span>
                    <ArrowRightOutlined className='arrow' />
                  </AttentionItem>
                ))}
                <AttentionItem to={`/${INBOX}`} className='inbox'>
                  <span className='text'>
                    <strong>{moreInInbox > 0 ? `${moreInInbox} more in Inbox` : 'Open Inbox'}</strong>
                  </span>
                  <ArrowRightOutlined className='arrow' />
                </AttentionItem>
              </>
            )}
          </Attention>
        )}

        <TopRow>
          <Card>
            <CardHead>
              <Segmented role='tablist' aria-label='Chart metric'>
                {(['value', 'revenue', 'volume'] as HeroMetric[]).map((m) => (
                  <button
                    key={m}
                    type='button'
                    role='tab'
                    aria-selected={hero === m}
                    className={hero === m ? 'active' : ''}
                    onClick={() => setHero(m)}
                  >
                    {m === 'value' ? 'Value' : m === 'revenue' ? 'Revenue' : 'Transactions'}
                  </button>
                ))}
              </Segmented>
            </CardHead>
            <HeroFigure>
              <span className='label'>
                {heroConfig.label} {RANGE_LABEL[range]}
                <InfoTip>{heroConfig.hint}</InfoTip>
              </span>
              <span className='value'>{heroConfig.total}</span>
              {heroConfig.trend !== undefined && (
                <span className={`trend ${heroConfig.trend >= 0 ? 'up' : 'down'}`}>
                  {trendText(heroConfig.trend)} <span>vs previous period</span>
                </span>
              )}
            </HeroFigure>
            <AreaTrendChart data={heroConfig.points} formatValue={heroConfig.format} />
          </Card>

          <Card>
            <CardHead>
              <CardTitle>Outcomes</CardTitle>
              <ButtonLink to={`/${TRANSACTIONS}`} className='more'>
                View all
              </ButtonLink>
            </CardHead>
            <HeroFigure>
              <span className='label'>
                Success rate
                <InfoTip>
                  Successful ÷ (successful + failed). Purchases still being confirmed don’t count
                  until they settle.
                </InfoTip>
              </span>
              <span className='value'>{(overview?.success_rate ?? 0).toFixed(1)}%</span>
              {overview?.trends.success_rate !== undefined && (
                <span className={`trend ${overview.trends.success_rate >= 0 ? 'up' : 'down'}`}>
                  {trendText(overview.trends.success_rate)} <span>vs previous period</span>
                </span>
              )}
            </HeroFigure>
            <StackBar aria-hidden>
              {total > 0 &&
                outcomes.map((o) =>
                  o.value > 0 ? (
                    <div key={o.key} style={{ flex: o.value, background: o.color }} />
                  ) : null
                )}
            </StackBar>
            <Legend>
              {outcomes.map((o) => (
                <li key={o.key}>
                  <span className='dot' style={{ background: o.color }} />
                  <span className='name'>{o.label}</span>
                  <strong>{formatNumber(o.value)}</strong>
                  <span className='pct'>{total ? `${Math.round((o.value / total) * 100)}%` : '—'}</span>
                </li>
              ))}
            </Legend>
          </Card>
        </TopRow>

        <Grid4>
          <StatsCard.Root>
            <StatsCard.Header hint='What Hethera kept from successful purchases after the Paystack fee and VTpass cost. Service fees are the ₦100 added to cable and electricity.'>
              Revenue
            </StatsCard.Header>
            <StatsCard.Value>{naira(overview?.total_revenue)}</StatsCard.Value>
            <StatsCard.PlainFooter>
              incl. {naira(overview?.service_fees_collected)} service fees
            </StatsCard.PlainFooter>
          </StatsCard.Root>
          <StatsCard.Root>
            <StatsCard.Header hint='Customers who started at least one purchase in this period, out of everyone who has ever bought.'>
              Active customers
            </StatsCard.Header>
            <StatsCard.Value>{formatNumber(overview?.active_customers ?? 0)}</StatsCard.Value>
            <StatsCard.PlainFooter>
              of {formatNumber(overview?.total_customers ?? 0)} total ·{' '}
              <TrendInline className={(overview?.trends.active_customers ?? 0) >= 0 ? 'up' : 'down'}>
                {trendText(overview?.trends.active_customers)}
              </TrendInline>
            </StatsCard.PlainFooter>
          </StatsCard.Root>
          <StatsCard.Root>
            <StatsCard.Header hint='The typical size of a successful purchase: transaction value ÷ successful purchases.'>
              Average purchase
            </StatsCard.Header>
            <StatsCard.Value>{naira(avgPurchase)}</StatsCard.Value>
            <StatsCard.PlainFooter>Per successful purchase</StatsCard.PlainFooter>
          </StatsCard.Root>
          <StatsCard.Root>
            <StatsCard.Header hint='Every bill purchase started in this period, whatever happened to it.'>
              Transactions
            </StatsCard.Header>
            <StatsCard.Value>{formatNumber(total)}</StatsCard.Value>
            <StatsCard.PlainFooter>
              <TrendInline className={(overview?.trends.total_transactions ?? 0) >= 0 ? 'up' : 'down'}>
                {trendText(overview?.trends.total_transactions)}
              </TrendInline>{' '}
              vs previous period
            </StatsCard.PlainFooter>
          </StatsCard.Root>
        </Grid4>

        <Section>
          <SectionHead>
            <h2>Profit</h2>
            <ButtonLink to={`/${PROFIT}`} className='more'>
              Open Profit <ArrowRightOutlined />
            </ButtonLink>
          </SectionHead>
          <Grid4>
            <ProfitTiles />
          </Grid4>
        </Section>

        <TwoUp>
          <Card>
            <CardHead>
              <CardTitle>By service</CardTitle>
            </CardHead>
            <RankedBars
              items={(byType?.items ?? []).map((item) => ({
                label: words(item.label),
                value: item.count,
                detail: naira(item.value),
              }))}
            />
          </Card>
          <Card>
            <CardHead>
              <CardTitle>By provider</CardTitle>
            </CardHead>
            <RankedBars
              items={(byProvider?.items ?? []).map((item) => ({
                label: item.label,
                value: item.count,
                detail: naira(item.value),
              }))}
            />
          </Card>
        </TwoUp>

        <FeatureMetrics overview={overview} />

        <Section>
          <SectionHead>
            <h2>Recent transactions</h2>
            <ButtonLink to={`/${TRANSACTIONS}`} className='more'>
              View all <ArrowRightOutlined />
            </ButtonLink>
          </SectionHead>
          <Table
            tableHeader={{
              Reference: 'reference',
              Customer: 'chat_id',
              Type: 'type',
              Amount: 'amount',
              Status: 'status',
              Date: 'created_at',
            }}
            tableDataSourceUrl='api/v1/admin/transactions'
            size={6}
            showPaginationControl={false}
            border
            cellsCustomComp={{
              chat_id: <CustomerLinkCell />,
              amount: <TableMoneyFormatter dataKey='amount' />,
              status: <StatusCell statusKey='status' hasDot positiveType />,
              created_at: <TableDateTimeFormatter dataKey='created_at' />,
            }}
            clickRowShowSliderComp={(row) => <TransactionDetailSlider data={row} />}
            hasClickableRow
            emptyStateMsg='No transactions yet'
          />
        </Section>
      </PageLayout>
    </Page>
  );
};

export default Dashboard;

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
`;

const Eyebrow = styled.div`
  font-size: 13px;
  color: #7a8279;
  margin-bottom: 4px;
`;

const Greeting = styled.h1`
  margin: 0;
  font-weight: 700;
  font-size: clamp(24px, 2.4vw, 30px);
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ink);
`;

const Attention = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AllClear = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(31, 122, 69, 0.07);
  color: #1f7a45;
  font-size: 13.5px;
  font-weight: 500;
`;

const AttentionItem = styled(ButtonLink)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: #ffffff;
  box-shadow: var(--shadow-xs);
  font-size: 13.5px;
  color: var(--ink);
  transition:
    border-color 0.15s ease,
    transform 0.15s ease;

  & .icon {
    font-size: 14px;
  }
  &.red .icon {
    color: var(--red);
  }
  &.amber .icon {
    color: #d9a21b;
  }
  &.inbox {
    background: var(--ink);
    border-color: var(--ink);
    color: #ffffff;
  }
  &.inbox .arrow,
  &.inbox:hover .arrow {
    color: #ffffff;
  }
  & .text {
    display: inline-flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  & .text strong {
    font-weight: 600;
  }
  & .text span {
    color: #7a8279;
  }
  & .arrow {
    font-size: 11px;
    color: #a9a395;
    transition: transform 0.15s ease;
  }

  &:hover {
    border-color: var(--line-2);
  }
  &:hover .arrow {
    transform: translateX(2px);
    color: var(--ink);
  }
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }

  & .more {
    font-size: 13px;
    color: var(--ok);
  }
`;

const HeroFigure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  & .label {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    color: var(--muted);
  }
  & .value {
    font-family: var(--font-display);
    font-size: clamp(28px, 3.2vw, 36px);
    line-height: 1.15;
    font-weight: 700;
    letter-spacing: -0.03em;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
    overflow-wrap: anywhere;
  }
  & .trend {
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  & .trend span {
    font-weight: 400;
    color: #7a8279;
  }
  & .trend.up {
    color: #1f7a45;
  }
  & .trend.down {
    color: var(--red);
  }
`;

const TrendInline = styled.span`
  font-weight: 600;
  &.up {
    color: #1f7a45;
  }
  &.down {
    color: var(--red);
  }
`;

const StackBar = styled.div`
  display: flex;
  gap: 2px;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: #efece4;
  margin-top: 4px;
`;

const Legend = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;

  & li {
    display: grid;
    grid-template-columns: 10px 1fr auto 44px;
    align-items: center;
    gap: 10px;
    padding: 10px 0;
    font-size: 13.5px;
    border-top: 1px solid #efece4;
  }
  & li:first-of-type {
    border-top: 0;
  }
  & .dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
  }
  & .name {
    color: var(--ink-2);
  }
  & strong {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  & .pct {
    text-align: right;
    color: #7a8279;
    font-variant-numeric: tabular-nums;
  }
`;

const Grid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  & h2 {
    margin: 0;
    font-family: var(--font-body);
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--ink);
  }
  & .more {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--ok);
  }
`;
